import IconButton from '@/global/components/Button/IconButton'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import ColumnEnum from '@/global/components/DataGrid/utils/columEnum'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import { Tooltip } from '@/global/components/ui/tooltip'
import { Ajuste } from '@/service/api'
import { CategoriaEnum } from '@acme/auth'
import { cpfMask, cpfValido, parserRegisterOrigin } from '@acme/common'
import { StatusAprovacao } from '@acme/core'
import dayjs from 'dayjs'
import { CheckIcon, XIcon } from 'lucide-react'
import { match } from 'ts-pattern'
import { Status } from '../atoms/StatusChip'
import VisualizarAnexoModal from '../atoms/VisualizarAnexo'

const columns: GridColDefPro[] = [
    {
        field: 'tipoAjuste',
        headerName: 'Tipo',
        width: 120,
        type: new ColumnEnum([
            {
                value: 'Abono',
                label: 'Abono',
            },
            {
                value: 'Afastamento',
                label: 'Afastamento',
            },
            {
                value: 'Registro',
                label: 'Registro',
            },
        ]),
    },
    {
        field: 'data',
        headerName: 'Data',
        width: 180,
        type: 'date',
    },
    {
        field: 'nome',
        headerName: 'Funcionário',
        minWidth: 140,
        flex: 1,
        valueGetter: ({ row }) => row.Funcionario.nome,
    },
    {
        field: 'hora',
        headerName: 'Hora',
        width: 60,
        type: 'time',
        valueFormatter: params => {
            if (!params.value) return ''
            const [hora, minuto] = params.value.split(':')

            return `${hora}:${minuto}`
        },
    },
    {
        field: 'origem',
        headerName: 'Origem',
        width: 150,
        valueGetter: ({ value }) => parserRegisterOrigin(value).title,
    },
    {
        field: 'justificativa',
        headerName: 'Justificativa',
        flex: 0.6,
        minWidth: 100,
        valueGetter: ({ row }) => row.justificativa || row.descricao,
    },
    {
        field: 'fileUrl',
        headerName: 'Anexo',
        width: 100,
        renderCell: VisualizarAnexoModal,
    },
]

export const columnsAprovacao: GridColDefPro<Ajuste & { canEdit: boolean }>[] =
    [
        ...columns,
        {
            field: 'statusAprovacao',
            headerName: 'Status',
            align: 'center',
            headerAlign: 'center',
            width: 168,
            type: new ColumnEnum([
                { value: StatusAprovacao.PENDENTE, label: 'Pendente' },
                {
                    value: StatusAprovacao.PENDENTE_EXCLUSAO,
                    label: 'Exclusão Pendente',
                },
            ]),
            renderCell: Status,
        },
        {
            ...optionsColumn,
            field: '__aprovar',
            align: 'right',
            renderCell: ({ id, row: { canEdit } }) =>
                canEdit && (
                    <IconButton
                        title="Aprovar"
                        href={`${String(id).split('_').at(0)}/${String(id)
                            .split('_')
                            .at(1)}/aprovar`}
                    >
                        <CheckIcon className="text-green-500" />
                    </IconButton>
                ),
        },
        {
            ...optionsColumn,
            field: '__reprovar',
            align: 'right',
            renderCell: ({ id, row: { canEdit } }) =>
                canEdit && (
                    <IconButton
                        title="Reprovar"
                        href={`${String(id).split('_').at(0)}/${String(id)
                            .split('_')
                            .at(1)}/reprovar`}
                    >
                        <XIcon className="text-red-500" />
                    </IconButton>
                ),
        },
    ]

export const columnsRelatorio: GridColDefPro[] = [
    {
        field: 'Criador',
        headerName: 'Incluído por',
        type: new ColumnEnum([
            { value: 'Funcionário', label: 'Funcionário' },
            { value: 'Supervisor', label: 'Supervisor' },
            { value: 'Administrador', label: 'Administrador' },
            { value: 'Desconhecido', label: 'Desconhecido' },
        ]),
        renderCell: ({ value }) => {
            if (!value) return 'Desconhecido'

            const title = cpfValido(value.email)
                ? cpfMask(value.email)
                : value.email

            return (
                <Tooltip title={title}>
                    <span>
                        {match(value.categoriaId)
                            .with(
                                CategoriaEnum.Funcionario,
                                () => 'Funcionário',
                            )
                            .with(CategoriaEnum.Supervisor, () => 'Supervisor')
                            .with(
                                CategoriaEnum.Cliente,
                                CategoriaEnum.Avaliacao,
                                () => 'Administrador',
                            )
                            .otherwise(() => 'Desconhecido')}
                    </span>
                </Tooltip>
            )
        },
    },
    ...columns,
    {
        field: 'statusAprovacao',
        headerName: 'Status',
        align: 'center',
        headerAlign: 'center',
        width: 200,
        type: new ColumnEnum([
            { value: StatusAprovacao.APROVADO, label: 'Aprovado' },
            { value: StatusAprovacao.REPROVADO, label: 'Reprovado' },
        ]),
        renderCell: Status,
    },
    {
        field: 'updatedAt',
        headerName: 'Data de aprovação',
        width: 150,
        type: 'date',
        valueFormatter: params => {
            if (!params.value) return ''
            return dayjs(params.value).format('DD/MM/YYYY HH:mm')
        },
    },
]
